import { useEffect } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Col, Form, Input, Row } from 'antd'
import type { AvatarConfig } from '@super-mega/server/contracts/profile'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { AvatarPicker } from './avatar-picker.tsx'

type PersonalSettingsFormValues = {
  name: string
  phone?: string
  avatar: AvatarConfig | null
}

type PersonalSettingsModalProps = {
  open: boolean
  onClose: () => void
}

export function PersonalSettingsModal({ onClose, open }: PersonalSettingsModalProps) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const [form] = Form.useForm<PersonalSettingsFormValues>()
  const displayName = Form.useWatch('name', form) || '用户'
  const profile = useQuery({
    ...trpc.profile.get.queryOptions(),
    enabled: open,
    retry: false
  })
  const updateProfile = useMutation(trpc.profile.update.mutationOptions())

  useEffect(() => {
    if (!open || !profile.data) {
      return
    }
    form.setFieldsValue({
      avatar: profile.data.avatar,
      name: profile.data.name,
      phone: profile.data.phone ?? ''
    })
  }, [form, open, profile.data])

  const save = async (values: PersonalSettingsFormValues) => {
    try {
      await updateProfile.mutateAsync({
        avatar: values.avatar ?? null,
        name: values.name.trim(),
        phone: values.phone?.trim() || null
      })
      await Promise.all([
        queryClient.invalidateQueries(trpc.profile.get.queryFilter()),
        queryClient.invalidateQueries(trpc.auth.session.queryFilter())
      ])
      void message.success('个人资料已更新')
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '个人资料保存失败'))
      return false
    }
  }

  return (
    <ModalForm<PersonalSettingsFormValues>
      form={form}
      layout="vertical"
      modalProps={{
        destroyOnHidden: true,
        loading: profile.isLoading,
        mask: { closable: false },
        onCancel: onClose
      }}
      onFinish={save}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose()
        }
      }}
      open={open}
      scrollToFirstError={{ focus: true }}
      submitter={{
        resetButtonProps: { onClick: onClose },
        searchConfig: { resetText: '取消', submitText: '保存设置' },
        submitButtonProps: {
          disabled: !profile.data,
          loading: updateProfile.isPending
        }
      }}
      title="个人设置"
      width="46rem"
    >
      {profile.isError && !profile.data ? (
        <Alert
          action={
            <Button onClick={() => void profile.refetch()} size="small">
              重新加载
            </Button>
          }
          description={getErrorMessage(profile.error, '个人资料加载失败，请稍后重试')}
          showIcon
          title="无法加载个人资料"
          type="error"
        />
      ) : (
        <>
          <Form.Item label="头像" name="avatar">
            <AvatarPicker displayName={displayName} />
          </Form.Item>

          <Row gutter={16}>
            <Col sm={12} xs={24}>
              <Form.Item
                label="姓名"
                name="name"
                rules={[
                  { message: '请输入姓名', required: true, whitespace: true },
                  { min: 2, message: '姓名长度不能少于 2 个字符' },
                  { max: 80, message: '姓名长度不能超过 80 个字符' }
                ]}
              >
                <Input autoComplete="name" maxLength={80} placeholder="请输入姓名" />
              </Form.Item>
            </Col>
            <Col sm={12} xs={24}>
              <Form.Item label="手机号码" name="phone" rules={[{ max: 30, message: '手机号码长度不能超过 30 个字符' }]}>
                <Input autoComplete="tel" maxLength={30} placeholder="请输入手机号码" />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
    </ModalForm>
  )
}
